import type { SupabaseClient } from '@supabase/supabase-js'
import {
  ASSIGNABLE_PLAN_STATUSES,
  BILLING_CHANNELS,
  PLAN_STATUSES,
  type BillingChannel,
  type PlanStatus,
} from './constants'
import type { BillingPlan } from './types'

export const DEFAULT_FREE_PLAN_NAMES = {
  [BILLING_CHANNELS.STRIPE]: 'free-stripe',
  [BILLING_CHANNELS.SHOPIFY]: 'free-shopify',
} as const

export type DefaultFreePlanName =
  (typeof DEFAULT_FREE_PLAN_NAMES)[keyof typeof DEFAULT_FREE_PLAN_NAMES]

const DEFAULT_FREE_PLAN_NAME_SET = new Set<string>(Object.values(DEFAULT_FREE_PLAN_NAMES))

export function getDefaultFreePlanName(billingChannel: BillingChannel): DefaultFreePlanName {
  return DEFAULT_FREE_PLAN_NAMES[billingChannel]
}

export function isDefaultFreePlanName(name: string): name is DefaultFreePlanName {
  return DEFAULT_FREE_PLAN_NAME_SET.has(name)
}

export function isSystemFreePlan(plan: Pick<BillingPlan, 'name'>): boolean {
  return isDefaultFreePlanName(plan.name)
}

export function getExpectedChannelForDefaultFreePlan(name: DefaultFreePlanName): BillingChannel {
  return name === DEFAULT_FREE_PLAN_NAMES[BILLING_CHANNELS.STRIPE]
    ? BILLING_CHANNELS.STRIPE
    : BILLING_CHANNELS.SHOPIFY
}

export async function resolveDefaultFreePlan(
  supabase: SupabaseClient,
  billingChannel: BillingChannel,
  options: { statuses?: PlanStatus[] } = {},
): Promise<BillingPlan | null> {
  const statuses = options.statuses ?? [PLAN_STATUSES.PUBLISHED]
  const { data, error } = await supabase
    .from('billing_plans')
    .select('*')
    .eq('name', getDefaultFreePlanName(billingChannel))
    .eq('billing_channel', billingChannel)
    .in('status', statuses)
    .single()

  if (error || !data) return null
  return data as BillingPlan
}

export function validateSystemFreePlanCreate(body: Record<string, unknown>): string | null {
  if (typeof body.name !== 'string' || !isDefaultFreePlanName(body.name)) {
    return null
  }

  const expectedChannel = getExpectedChannelForDefaultFreePlan(body.name)
  const effectiveChannel = typeof body.billing_channel === 'string'
    ? body.billing_channel
    : BILLING_CHANNELS.STRIPE
  const effectivePrice = typeof body.price_monthly_cents === 'number'
    ? body.price_monthly_cents
    : 0
  const effectiveStatus = typeof body.status === 'string'
    ? body.status
    : PLAN_STATUSES.DRAFT

  if (effectiveChannel !== expectedChannel) {
    return `System free plan ${body.name} must keep billing_channel='${expectedChannel}'`
  }
  if (effectivePrice !== 0) {
    return `System free plan ${body.name} must keep price_monthly_cents=0`
  }
  // System free plans may live as either `internal` (super-user-only,
  // hidden from public pricing + tenant self-serve registration) or
  // `published` (visible everywhere). Both are valid operational states;
  // `draft` / `archived` would break the canonical fallback role
  // (`grace-period` CRON, admin tenant create) so those stay forbidden.
  if (!(ASSIGNABLE_PLAN_STATUSES as readonly string[]).includes(effectiveStatus)) {
    return `System free plan ${body.name} status must be 'internal' or 'published' (got '${effectiveStatus}')`
  }

  return null
}

export function validateSystemFreePlanMutation(
  existing: Pick<BillingPlan, 'name' | 'billing_channel' | 'price_monthly_cents' | 'status'>,
  patch: Record<string, unknown>,
): string | null {
  if (!isDefaultFreePlanName(existing.name)) {
    return null
  }

  const expectedChannel = getExpectedChannelForDefaultFreePlan(existing.name)
  const effectiveName = typeof patch.name === 'string' ? patch.name : existing.name
  const effectiveChannel = typeof patch.billing_channel === 'string'
    ? patch.billing_channel
    : existing.billing_channel
  const effectivePrice = typeof patch.price_monthly_cents === 'number'
    ? patch.price_monthly_cents
    : existing.price_monthly_cents
  const effectiveStatus = typeof patch.status === 'string'
    ? patch.status
    : existing.status

  if (effectiveName !== existing.name) {
    return `System free plan ${existing.name} cannot be renamed`
  }
  if (effectiveChannel !== expectedChannel) {
    return `System free plan ${existing.name} must keep billing_channel='${expectedChannel}'`
  }
  if (effectivePrice !== 0) {
    return `System free plan ${existing.name} must keep price_monthly_cents=0`
  }
  // System free plans may live as either `internal` (super-user-only) or
  // `published` (also visible to public registration). Both are equivalent
  // for the operational role (grace-period downgrade target, admin
  // assignment, tenant bootstrap). Block only `draft` / `archived` so the
  // canonical fallback never disappears.
  if (!(ASSIGNABLE_PLAN_STATUSES as readonly string[]).includes(effectiveStatus)) {
    return `System free plan ${existing.name} status must be 'internal' or 'published' (got '${effectiveStatus}')`
  }

  return null
}
